// F-072, F-073, F-076 : congé et calendrier de départ, visites, clôture et relance d'une campagne.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { get, patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, ButtonLink, Card, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { BackLink, DEPARTURE_STATUS, DocLinks, RuleNote, Status, VISIT_STATUS, meLease, orgApi, orgPath, sendFile, toIso, todayIso, useAction } from "./shared";
import { DepositPanel, TenantDeposit } from "./deposit";
import { TenantKeys } from "./keys";

type Catalog = { rule_version: string; reasons: Record<string, { value: string; label: string }[]>; channels: { value: string; label: string }[]; visit_reminder: string };

/** Calcul proposé : fondement, avertissements, références, version des règles. */
function Computation({ c }: { c: any }) {
  if (!c) return null;
  return (
    <div className="stack-sm">
      <KeyValues
        items={[
          ["Préavis", c.months ? `${c.months} mois` : "—"],
          ["Point de départ", c.start_on ? `${date(c.start_on)}${c.provisional ? " (provisoire : date d’envoi)" : ""}` : "Date de réception à indiquer"],
          ["Fin proposée", c.end_on ? date(c.end_on) : "—"],
          ...(c.term_end_on ? ([["Terme du bail visé", date(c.term_end_on)]] as [string, string][]) : []),
        ]}
      />
      <RuleNote version={c.rule_version}>{c.basis}</RuleNote>
      {c.warnings?.map((w: string) => (
        <Notice key={w} tone="attention">
          {w}
        </Notice>
      ))}
      {c.references?.length ? <p className="small muted">Références : {c.references.join(" ; ")}</p> : null}
    </div>
  );
}

export function DeparturesPage() {
  const org = useOrg();
  const [all, setAll] = useState(false);
  const q = useGet<{ items: any[] }>(orgApi(org.orgId, `/departures${all ? "?all=true" : ""}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Vie du logement"
        title="Départs"
        intro="Congés reçus ou donnés, calendrier de départ, visites, dépôt de garantie et relocation."
        actions={<ButtonLink to={org.to("passations")} variant="secondaire">Passation d’un logement</ButtonLink>}
      />
      <p>
        <button type="button" className="vie-link" onClick={() => setAll(!all)}>
          {all ? "Voir seulement les départs en cours" : "Voir aussi les départs clos et annulés"}
        </button>
      </p>
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        <Table caption="Départs">
          <thead>
            <tr>
              <th>Logement</th>
              <th>Congé</th>
              <th>Statut</th>
              <th>Fin</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((d) => (
              <tr key={d.id}>
                <td>
                  <Link to={org.to(`departs/${d.id}`)}>{d.property_label}</Link>
                </td>
                <td>
                  {d.labels.initiated_by}
                  <span className="block small muted">{d.labels.reason}</span>
                </td>
                <td>
                  <Status value={d.status} labels={DEPARTURE_STATUS} />
                </td>
                <td className="num">{d.confirmed_end_on ? date(d.confirmed_end_on) : d.computed_end_on ? `${date(d.computed_end_on)} (proposée)` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Empty title="Aucun départ en cours">Enregistrez un congé depuis l’onglet « Départ et dépôt » du bail.</Empty>
      )}
    </div>
  );
}

/** Formulaire d'enregistrement d'un congé par le bailleur, avec aperçu du calcul. */
function NoticeForm({ orgId, leaseId, catalog, onDone }: { orgId: string; leaseId: string; catalog: Catalog; onDone: (id: string) => void }) {
  const [f, setF] = useState({ initiated_by: "locataire", reason: "standard", channel: "recommande", sent_on: "", received_on: todayIso(), notes: "" });
  const [preview, setPreview] = useState<any>(null);
  const act = useAction();
  useEffect(() => {
    const params = new URLSearchParams({ initiated_by: f.initiated_by, reason: f.reason, channel: f.channel });
    if (f.received_on) params.set("received_on", f.received_on);
    if (f.sent_on) params.set("sent_on", f.sent_on);
    get(orgApi(orgId, `/leases/${leaseId}/notice-preview?${params}`))
      .then((r: any) => setPreview(r.computation))
      .catch(() => setPreview(null));
  }, [orgId, leaseId, f.initiated_by, f.reason, f.channel, f.received_on, f.sent_on]);
  const reasons = catalog.reasons[f.initiated_by] ?? [];
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await act.run(() => post<{ id: string }>(orgApi(orgId, `/leases/${leaseId}/departures`), { ...f, sent_on: f.sent_on || undefined, received_on: f.received_on || undefined }), "Congé enregistré. Confirmez la date de fin après examen.");
        if (r) onDone(r.id);
      }}
    >
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Select
          label="Congé donné par"
          value={f.initiated_by}
          onChange={(e) => setF({ ...f, initiated_by: e.target.value, reason: catalog.reasons[e.target.value]?.[0]?.value ?? "" })}
          options={[
            { value: "locataire", label: "Le locataire" },
            { value: "bailleur", label: "Le bailleur" },
            { value: "accord", label: "Départ d’un commun accord" },
          ]}
        />
        <Select label="Motif" value={f.reason} error={fieldError(act.error, "reason")} options={reasons} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        <Select label="Mode de délivrance" value={f.channel} options={catalog.channels} onChange={(e) => setF({ ...f, channel: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Envoyé le" type="date" value={f.sent_on} onChange={(e) => setF({ ...f, sent_on: e.target.value })} />
        <Input label="Reçu le" type="date" value={f.received_on} error={fieldError(act.error, "received_on")} help="Point de départ du délai : réception de la lettre, signification ou remise en main propre." onChange={(e) => setF({ ...f, received_on: e.target.value })} />
      </div>
      <Textarea label="Notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <Card title="Calcul proposé">
        <Computation c={preview} />
      </Card>
      <div>
        <Button type="submit" busy={act.busy}>
          Enregistrer le congé
        </Button>
      </div>
    </form>
  );
}

/** Détail d'un départ : examen, confirmation, calendrier, visites, dépôt, relance, clôture. */
function DepartureDetail({ orgId, data, onChange }: { orgId: string; data: any; onChange: () => void }) {
  const org = useOrg();
  const d = data.departure;
  const base = orgApi(orgId, `/departures/${d.id}`);
  const [received, setReceived] = useState(d.received_on ?? "");
  const [confirmForm, setConfirmForm] = useState({ end_on: d.computed_end_on ?? "", review_notes: "" });
  const [logistics, setLogistics] = useState({ exit_inspection_at: "", keys_returned_on: d.keys_returned_on ?? "" });
  const [visit, setVisit] = useState({ day: "", from: "18:00", to: "18:30", note: "" });
  const [relaunch, setRelaunch] = useState<any>(null);
  const act = useAction();
  const open = ["recu", "confirme"].includes(d.status);
  return (
    <div className="stack">
      <ErrorBox error={act.error} />
      <div className="grid-2">
        <Card title="Congé">
          <KeyValues
            items={[
              ["Donné par", data.labels.initiated_by],
              ["Motif", data.labels.reason],
              ["Mode", data.labels.channel],
              ["Envoyé le", date(d.sent_on)],
              ["Reçu le", date(d.received_on)],
              ["Locataires", data.tenants.join(", ") || "—"],
              ["Fin confirmée", d.confirmed_end_on ? date(d.confirmed_end_on) : "Non confirmée"],
            ]}
          />
          {d.review_notes ? <p className="small">Examen : {d.review_notes}</p> : null}
          <DocLinks docs={data.documents} />
          {open && org.can("operate") ? (
            <FileDrop
              label="Ajouter la lettre, l’accusé de réception ou un justificatif"
              onFiles={async (files) => {
                for (const f of files) await act.run(() => sendFile(`${base}/documents`, f, { kind: "accuse_reception" }), "Pièce ajoutée.");
                onChange();
              }}
            />
          ) : null}
        </Card>
        <Card title="Calcul proposé">
          <Computation c={d.computation} />
        </Card>
      </div>
      {d.status === "recu" ? (
        <Card title="Examiner et confirmer">
          <div className="stack">
            {org.can("operate") ? (
              <form
                className="row"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const r: any = await act.run(() => patch(base, { received_on: received }), "Date de réception enregistrée, calcul mis à jour.");
                  if (r) {
                    setConfirmForm({ ...confirmForm, end_on: r.departure.computed_end_on ?? "" });
                    onChange();
                  }
                }}
              >
                <Input label="Congé reçu le" type="date" value={received} error={fieldError(act.error, "received_on")} onChange={(e) => setReceived(e.target.value)} />
                <Button type="submit" variant="secondaire" small>
                  Recalculer
                </Button>
              </form>
            ) : null}
            {org.can("decide") ? (
              <form
                className="stack-sm"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (await act.run(() => post(`${base}/confirm`, confirmForm), "Date de fin confirmée : le bail passe en préavis.")) onChange();
                }}
              >
                <Input label="Date de fin retenue" type="date" value={confirmForm.end_on} error={fieldError(act.error, "end_on")} onChange={(e) => setConfirmForm({ ...confirmForm, end_on: e.target.value })} />
                <Textarea
                  label="Examen (obligatoire si la date diffère de la proposition)"
                  rows={2}
                  value={confirmForm.review_notes}
                  error={fieldError(act.error, "review_notes")}
                  onChange={(e) => setConfirmForm({ ...confirmForm, review_notes: e.target.value })}
                />
                <p className="small muted">Vous confirmez la date après examen de la lettre et des justificatifs. Le calcul de Bailivo est une proposition fondée sur des règles à valider.</p>
                <div>
                  <Button type="submit" busy={act.busy}>
                    Confirmer la date de fin
                  </Button>
                </div>
              </form>
            ) : (
              <p className="small muted">La confirmation est réservée aux membres qui décident.</p>
            )}
          </div>
        </Card>
      ) : null}
      {d.status === "confirme" ? (
        <>
          <Card title="Calendrier de départ">
            <div className="stack">
              {data.tasks.length ? (
                <ul className="list-plain">
                  {data.tasks.map((t: any) => (
                    <li key={t.id}>
                      <Status value={t.status} label={t.status === "fait" ? "Fait" : t.status === "annule" ? "Annulé" : "À faire"} /> {t.title} <span className="small muted">· {date(t.due_on)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {org.can("operate") ? (
                <form
                  className="form-row"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (
                      await act.run(
                        () => patch(base, { exit_inspection_at: logistics.exit_inspection_at ? toIso(logistics.exit_inspection_at) : undefined, keys_returned_on: logistics.keys_returned_on || undefined }),
                        "Calendrier mis à jour.",
                      )
                    )
                      onChange();
                  }}
                >
                  <Input label="État des lieux de sortie" type="datetime-local" value={logistics.exit_inspection_at} help={d.exit_inspection_at ? `Prévu le ${dateTime(d.exit_inspection_at)}` : "À réaliser avec le module d’entrée."} onChange={(e) => setLogistics({ ...logistics, exit_inspection_at: e.target.value })} />
                  <Input label="Clés remises le" type="date" value={logistics.keys_returned_on} error={fieldError(act.error, "keys_returned_on")} onChange={(e) => setLogistics({ ...logistics, keys_returned_on: e.target.value })} />
                  <Button type="submit" variant="secondaire">
                    Enregistrer
                  </Button>
                </form>
              ) : null}
            </div>
          </Card>
          <Card title="Visites">
            <div className="stack">
              <Notice tone="info">{data.visit_reminder}</Notice>
              {data.visits.length ? (
                <Table caption="Visites">
                  <thead>
                    <tr>
                      <th>Créneau</th>
                      <th>Statut</th>
                      <th>Rappels</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.visits.map((v: any) => (
                      <tr key={v.id}>
                        <td>
                          {dateTime(v.starts_at)} → {new Date(v.ends_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          {v.tenant_note ? <span className="block small muted">Locataire : {v.tenant_note}</span> : null}
                        </td>
                        <td>
                          <Status value={v.status} labels={VISIT_STATUS} />
                        </td>
                        <td className="small">{(v.warnings as string[]).join(" ") || "—"}</td>
                        <td>
                          {org.can("operate") && ["proposee", "acceptee"].includes(v.status) ? (
                            <div className="row">
                              {v.status === "acceptee" && new Date(v.starts_at) <= new Date() ? (
                                <Button small variant="secondaire" onClick={async () => { await act.run(() => patch(orgApi(orgId, `/visits/${v.id}`), { status: "realisee" }), "Visite effectuée."); onChange(); }}>
                                  Effectuée
                                </Button>
                              ) : null}
                              <Button small variant="discret" onClick={async () => { await act.run(() => patch(orgApi(orgId, `/visits/${v.id}`), { status: "annulee" }), "Visite annulée."); onChange(); }}>
                                Annuler
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="muted">Aucune visite proposée.</p>
              )}
              {org.can("operate") ? (
                <form
                  className="stack-sm"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const r: any = await act.run(
                      () => post(`${base}/visits`, { starts_at: toIso(`${visit.day}T${visit.from}`), ends_at: toIso(`${visit.day}T${visit.to}`), note: visit.note }),
                      "Créneau proposé au locataire.",
                    );
                    if (r) {
                      setVisit({ ...visit, note: "" });
                      onChange();
                    }
                  }}
                >
                  <h3>Proposer un créneau</h3>
                  <div className="form-row">
                    <Input label="Jour" type="date" required value={visit.day} onChange={(e) => setVisit({ ...visit, day: e.target.value })} />
                    <Input label="De" type="time" value={visit.from} onChange={(e) => setVisit({ ...visit, from: e.target.value })} />
                    <Input label="À" type="time" value={visit.to} error={fieldError(act.error, "ends_at") ?? fieldError(act.error, "starts_at")} onChange={(e) => setVisit({ ...visit, to: e.target.value })} />
                  </div>
                  <Input label="Information pour le locataire (sans donnée sur les visiteurs)" value={visit.note} onChange={(e) => setVisit({ ...visit, note: e.target.value })} />
                  <div>
                    <Button type="submit" variant="secondaire" busy={act.busy}>
                      Proposer
                    </Button>
                  </div>
                </form>
              ) : null}
            </div>
          </Card>
        </>
      ) : null}
      {["confirme", "termine"].includes(d.status) ? (
        <Card title="Relocation">
          {data.relaunch ? (
            <p>
              Une nouvelle campagne a été préparée ({data.relaunch.status}).{" "}
              <Link to={orgPath(orgId, `campagnes/${data.relaunch.campaign_id}`)}>Ouvrir la campagne</Link>
            </p>
          ) : org.can("operate") ? (
            <Button
              variant="secondaire"
              busy={act.busy}
              onClick={async () => {
                const r = await act.run(() => post(`${base}/relaunch`, {}), "Campagne préparée en brouillon.");
                if (r) {
                  setRelaunch(r);
                  onChange();
                }
              }}
            >
              Préparer une nouvelle campagne
            </Button>
          ) : null}
          {relaunch?.a_revoir ? (
            <div className="stack-sm">
              <p className="small">À revoir avant publication :</p>
              <ul className="small">
                {relaunch.a_revoir.map((x: string) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
              <p className="small">Non repris : {relaunch.non_repris.join(" ; ")}.</p>
            </div>
          ) : null}
        </Card>
      ) : null}
      <DepositPanel orgId={orgId} leaseId={d.lease_id} />
      {open && org.can("decide") ? (
        <div className="row">
          {d.status === "confirme" ? (
            <Button
              busy={act.busy}
              onClick={async () => {
                if (!confirm("Clôturer le départ ? Le bail passera au statut terminé et les accès délégués seront révoqués.")) return;
                if (await act.run(() => post(`${base}/close`, {}), "Départ clôturé, bail terminé.")) onChange();
              }}
            >
              Clôturer le départ
            </Button>
          ) : null}
          <Button
            variant="danger"
            onClick={async () => {
              const reason = prompt("Motif de l’annulation (congé retiré, erreur…)") ?? "";
              if (await act.run(() => post(`${base}/cancel`, { reason }), "Départ annulé : le bail retrouve son état antérieur.")) onChange();
            }}
          >
            Annuler le départ
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function DeparturePage() {
  const org = useOrg();
  const { departureId = "" } = useParams();
  const path = orgApi(org.orgId, `/departures/${departureId}`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data.departure;
  return (
    <div className="page">
      <BackLink to={org.to("departs")}>Départs</BackLink>
      <PageHeader eyebrow="Départ" title={d.property_label} actions={<Status value={d.status} labels={DEPARTURE_STATUS} />} />
      <DepartureDetail orgId={org.orgId} data={q.data} onChange={() => invalidate(path, orgApi(org.orgId, "/departures"), orgApi(org.orgId, "/leases"))} />
    </div>
  );
}

/** Onglet « Départ et dépôt » de la page du bail. */
export function LeaseDepartureTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const org = useOrg();
  const path = orgApi(orgId, `/leases/${leaseId}/departure`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const refresh = () => invalidate(path, orgApi(orgId, "/departures"), orgApi(orgId, `/leases/${leaseId}`));
  if (!q.data.current) {
    return (
      <div className="stack">
        {org.can("operate") && ["actif", "signe"].includes(q.data.lease.status) ? (
          <Card title="Enregistrer un congé">
            <NoticeForm orgId={orgId} leaseId={leaseId} catalog={q.data.catalog} onDone={refresh} />
          </Card>
        ) : (
          <Empty title="Aucun départ en cours" />
        )}
        <DepositPanel orgId={orgId} leaseId={leaseId} />
      </div>
    );
  }
  return (
    <div className="stack">
      <p>
        <Link to={orgPath(orgId, `departs/${q.data.current.departure.id}`)}>Ouvrir la fiche du départ</Link> <Badge>{DEPARTURE_STATUS[q.data.current.departure.status]}</Badge>
      </p>
      <DepartureDetail orgId={orgId} data={q.data.current} onChange={refresh} />
    </div>
  );
}

/** Section « Donner congé » côté locataire. */
export function TenantNoticeSection({ leaseId }: { leaseId: string }) {
  const path = meLease(leaseId, "/departure");
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const [f, setF] = useState({ reason: "standard", channel: "recommande", sent_on: todayIso(), notes: "" });
  const [preview, setPreview] = useState<any>(null);
  const [letter, setLetter] = useState<string>("");
  const [note, setNote] = useState("");
  const act = useAction();
  useEffect(() => {
    const params = new URLSearchParams({ reason: f.reason, channel: f.channel });
    if (f.sent_on) params.set("sent_on", f.sent_on);
    get(meLease(leaseId, `/notice-preview?${params}`))
      .then((r: any) => setPreview(r.computation))
      .catch(() => setPreview(null));
  }, [leaseId, f.reason, f.channel, f.sent_on]);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const data = q.data;
  const d = data.departure;
  const refresh = () => invalidate(path);
  return (
    <div className="stack">
      <ErrorBox error={act.error} />
      {!d || d.status === "annule" ? (
        <Card title="Donner congé">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const r: any = await act.run(() => post(path, { ...f, sent_on: f.sent_on || undefined }), "Congé enregistré. Votre lettre est prête.");
              if (r) {
                setLetter(r.letter_document_id);
                refresh();
              }
            }}
          >
            <Notice tone="info">{data.rappel}</Notice>
            <Select label="Motif" value={f.reason} options={data.catalog.reasons.locataire} onChange={(e) => setF({ ...f, reason: e.target.value })} />
            <Select label="Mode d’envoi prévu" value={f.channel} options={data.catalog.channels} onChange={(e) => setF({ ...f, channel: e.target.value })} />
            <Input label="Date d’envoi" type="date" value={f.sent_on} error={fieldError(act.error, "sent_on")} onChange={(e) => setF({ ...f, sent_on: e.target.value })} />
            <Card title="Estimation du préavis">
              <Computation c={preview} />
            </Card>
            <div>
              <Button type="submit" busy={act.busy}>
                Préparer ma lettre de congé
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <>
          <Card title="Mon départ" actions={<Status value={d.status} labels={DEPARTURE_STATUS} />}>
            <div className="stack-sm">
              <KeyValues
                items={[
                  ["Congé", `${data.labels.initiated_by} — ${data.labels.reason}`],
                  ["Envoyé le", date(d.sent_on)],
                  ["Reçu par le bailleur le", d.received_on ? date(d.received_on) : "Pas encore indiqué"],
                  ["Fin du bail", d.confirmed_end_on ? `${date(d.confirmed_end_on)} (confirmée)` : d.computed_end_on ? `${date(d.computed_end_on)} (estimation)` : "—"],
                  ["État des lieux de sortie", d.exit_inspection_at ? dateTime(d.exit_inspection_at) : "À fixer avec votre bailleur"],
                ]}
              />
              {letter || data.documents.length ? <DocLinks docs={data.documents} /> : null}
              <FileDrop
                label="Ajouter un justificatif ou l’accusé de réception"
                onFiles={async (files) => {
                  for (const file of files) await act.run(() => sendFile(meLease(leaseId, "/departure/documents"), file, { kind: "justificatif_motif" }), "Pièce ajoutée.");
                  refresh();
                }}
              />
            </div>
          </Card>
          <Card title="Visites du logement">
            <p className="small muted">{data.catalog.visit_reminder}</p>
            {data.visits.length ? (
              <ul className="list-plain">
                {data.visits.map((v: any) => (
                  <li key={v.id} className="vie-agenda-item">
                    <div>
                      <p>
                        <strong>{dateTime(v.starts_at)}</strong> → {new Date(v.ends_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                      {v.note ? <p className="small">{v.note}</p> : null}
                      {(v.warnings as string[]).map((w) => (
                        <p key={w} className="small muted">
                          {w}
                        </p>
                      ))}
                    </div>
                    {v.status === "proposee" && new Date(v.starts_at) > new Date() ? (
                      <div className="stack-sm">
                        <Input label="Message (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} />
                        <div className="row">
                          <Button small onClick={async () => { await act.run(() => post(meLease(leaseId, `/visits/${v.id}/respond`), { accept: true, note }), "Créneau accepté."); setNote(""); refresh(); }}>
                            J’accepte ce créneau
                          </Button>
                          <Button small variant="secondaire" onClick={async () => { await act.run(() => post(meLease(leaseId, `/visits/${v.id}/respond`), { accept: false, note }), "Créneau refusé."); setNote(""); refresh(); }}>
                            Je refuse
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Status value={v.status} labels={VISIT_STATUS} />
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Aucune visite proposée.</p>
            )}
          </Card>
        </>
      )}
      <TenantKeys leaseId={leaseId} />
      <TenantDeposit deposit={data.deposit} />
    </div>
  );
}
